<template>
  <div class="simple-text-page-content main-content wide">
    <div
      v-if="pageLeadImg !== ''"
      class="lead-img"
      :style="{ 'background-image': 'url(' + pageLeadImg + ')' }"
    />
    <div class="wrap">
      <div class="simple-text-page-main-block">
        <h1 :class="{ 'with-button': blueButtonLink && blueButtonText }">
          {{ pageTitle }}
        </h1>
        <div
          v-if="blueButtonLink && blueButtonText"
          class="btn btn-blue"
        >
          <a :href="blueButtonLink">{{ blueButtonText }}</a>
        </div>

        <p
          v-if="intro !== ''"
          class="lead-text"
        >
          {{ intro }}
        </p>

        <template v-for="(section, index) in sections">
          <simple-table
            v-if="section.type === 'table' && section.tableData"
            :key="index"
            :table-data="JSON.parse(section.tableData)"
          />

          <gygWidget
            v-else-if="section.type === 'gyg' && section.data"
            :key="index"
            :data="section.data"
          />

          <sixt-widget
            v-else-if="section.type === 'sixtWidget'"
            :key="index"
            :search-only="section.searchOnly"
          />

          <div
            v-else
            :key="index"
            v-dompurify-html="section"
            v-inlineModal
            class="block-wrap"
          />
        </template>
      </div>
    </div>

    <div class="wrap">
      <terms-and-conditions
        v-if="termsAndConditionsVisible"
        :title="termsAndConditions.title"
        :content="termsAndConditions.content"
      />
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import SimpleTable from 'components/Tables/SimpleTable'
  import GygWidget from 'components/Widgets/Gyg'
  import TermsAndConditions from 'components/TermsAndConditions'
  import InlineModal from 'utils/directives/InlineModal'
  import SixtWidget from 'components/Widgets/Sixt'

  export default {
    components: {
      SimpleTable,
      GygWidget,
      TermsAndConditions,
      SixtWidget
    },
    directives: {
      InlineModal
    },
    computed: {
      ...mapState('simpleTextPage', [
        'pageLeadImg',
        'pageTitle',
        'blueButtonLink',
        'blueButtonText',
        'intro',
        'sections',
        'tableData',
        'termsAndConditionsVisible',
        'termsAndConditions'
      ])
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .simple-text-page-content {
    padding: 0 0 16px;

    @include bp-tablet-only {
      padding-bottom: 40px;
    }

    @include bp-mf-desktop {
      padding-bottom: 90px;
    }

    .lead-img {
      height: 180px;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center center;

      @include bp-tablet-only {
        height: 444px;
      }

      @include bp-mf-desktop {
        height: 414px;
      }

      @media only screen and (min-width: 1400px) {
        height: 494px;
      }
    }
  }

  .wrap {
    padding: 0 23px;

    @include bp-tablet-only {
      padding: 0 26px;
    }

    @include bp-mf-desktop {
      margin: 0 auto;
      padding: 0;
      max-width: 740px;
    }
  }

  .simple-text-page-main-block {
    position: relative;

    h1 {
      margin: 19px 0 20px;
      font-size: em(32);
      line-height: 40px;
      font-weight: 300;
      word-wrap: break-word;

      @include bp-mf-tablet {
        margin: 34px 0 32px;
        font-size: em(40);
        line-height: 50px;
      }

      &.with-button {
        @include bp-mf-tablet {
          padding-right: 220px;
        }
      }

      + .lead-text {
        @include bp-mf-tablet {
          margin: -6px 0 8px;
        }
      }
    }

    .btn {
      margin-bottom: 28px;
      max-width: 180px;
      height: 48px;

      @include bp-tablet-only {
        right: 26px;
      }

      @include bp-mf-tablet {
        position: absolute;
        top: 0;
        margin-bottom: 0;
        height: 52px;
      }

      @include bp-mf-desktop {
        right: 0;
      }

      a {
        padding: 0 16px;
        font-size: em(14);
        line-height: 48px;

        @include bp-mf-tablet {
          font-size: em(16);
          line-height: 52px;
        }
      }

      + .lead-text {
        padding-top: 15px;
        border-top: 1px solid $grey-border;
      }
    }

    .lead-text {
      margin: 0;
      font-size: em(18);
      font-weight: 300;
      line-height: 26px;

      @include bp-mf-tablet {
        font-size: em(20);
        line-height: 28px;
      }
    }
  }

  .block-wrap {
    padding: 24px 0 23px;
    border-top: 1px solid $grey-border;

    @include bp-mf-tablet {
      padding-bottom: 24px;
    }

    h1 + & {
      padding-top: 0;
      border-top: none;
    }

    .lead-text + & {
      border-top: none;
    }

    .btn-blue + .lead-text + & {
      margin-top: 15px;
      border-top: 1px solid $grey-border;
    }

    ::v-deep h3 {
      margin: 0 0 13px;
      font-size: em(20);
      line-height: 24px;
      font-weight: 500;

      @include bp-mf-tablet {
        margin-bottom: 20px;
      }
    }

    ::v-deep p {
      margin: 0;
      font-size: em(14);
      line-height: 20px;

      @include bp-mf-tablet {
        font-size: em(16);
        line-height: 24px;
      }

      &:not(:last-child) {
        margin-bottom: 13px;

        @include bp-mf-tablet {
          margin-bottom: 10px;
        }
      }

      ::v-deep a {
        color: $blue-medium;

        &:hover {
          @include bp-mf-tablet {
            color: $blue-light;
          }
        }
      }
    }

    ::v-deep ul {
      list-style: none;

      + p {
        margin-top: 15px;

        @include bp-mf-tablet {
          margin-top: 18px;
        }
      }
    }

    ::v-deep strong {
      font-weight: 500;
    }

    ::v-deep li {
      position: relative;
      padding: 5px 0 6px 30px;
      font-size: em(14);
      line-height: 21px;

      @include bp-mf-tablet {
        padding: 6px 0 6px 26px;
        font-size: em(16);
        line-height: 24px;
      }

      &:last-child {
        padding-bottom: 0;
      }

      &:before {
        content: '';
        display: block;
        position: absolute;
        top: 11px;
        left: 3px;
        width: 8px;
        height: 8px;
        background-color: $green-base;
        border-radius: 50%;

        @include bp-mf-tablet {
          top: 12px;
          left: 0;
        }
      }
    }
  }

  .terms-and-conditions-block {
    margin: 30px 0 20px;

    .block-wrap + &:before {
      content: '';
      display: block;
      position: relative;
      top: -30px;
      width: 100%;
      height: 1px;
      background-color: $grey-border;
    }
  }
</style>
